import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Search } from "lucide-react";
import { invoicesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, EmptyState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function PortalInvoices() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => invoicesApi.list(), []);
  const [q, setQ] = useState("");
  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((x) => `${x.invoice_number || ""} ${x.building_name || ""}`.toLowerCase().includes(s));
  }, [list, q]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Your invoices" subtitle="Invoices raised for your buildings" testId="portal-invoices-header" />
      {loading && <LoadingState label="Loading invoices…" />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoices…" className="pl-9" data-testid="portal-invoices-search" />
          </div>
          {filtered.length === 0 ? (
            <EmptyState icon={Receipt} title="No invoices" message="Invoices for your buildings will appear here." />
          ) : (
            <div className="space-y-3">
              {filtered.map((x) => {
                const paid = x.status === "paid";
                return (
                  <Card key={x.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/portal/invoices/${x.id}`)} data-testid={`portal-invoice-row-${x.id}`}>
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-slate-900">{x.invoice_number}</p>
                      <p className="truncate text-sm text-slate-500">{x.building_name || "—"}{x.due_date ? ` · due ${fmtDate(x.due_date)}` : ""}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="font-semibold text-slate-800">{fmtMoney(x.total)}</span>
                      <Badge variant="outline" className={statusTone(x.overdue && !paid ? "overdue" : x.status)}>
                        {x.overdue && !paid ? `Overdue ${x.days_overdue}d` : prettyStatus(x.status)}
                      </Badge>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
